import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, Phone, FileDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ExpiredCustomersTable = ({ customers = [] }) => {
  const [expanded, setExpanded] = useState(false);

  const exportExcel = (e) => {
    e.stopPropagation();
    const data = customers.map(c => ({
      Username: c.username,
      Customer: c.name,
      Phone: c.phone || '',
      Package: c.package_name || '',
      Area: c.area_name || '',
      'Expiry Date': c.expiry_date,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Missed Renewals');
    XLSX.writeFile(wb, 'Missed-Renewals-This-Month.xlsx');
  };

  const exportPDF = (e) => {
    e.stopPropagation();
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text('Missed Renewals — This Month', 14, 16);
    autoTable(doc, {
      startY: 22,
      head: [['Username', 'Customer', 'Phone', 'Package', 'Area', 'Expiry Date']],
      body: customers.map(c => [
        c.username, c.name, c.phone || '—',
        c.package_name || '—', c.area_name || '—', c.expiry_date,
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [185, 28, 28] },
    });
    doc.save('Missed-Renewals-This-Month.pdf');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 border-b border-red-100 bg-red-50 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-800" />
          <h3 className="font-bold text-sm sm:text-base text-red-800">
            Missed Renewals — This Month
          </h3>
          <span className="text-xs font-bold px-2 py-1 rounded-full bg-red-200 text-red-800">
            {customers.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {customers.length > 0 && (
            <>
              <button
                onClick={exportExcel}
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors"
              >
                <FileDown size={12} /> Excel
              </button>
              <button
                onClick={exportPDF}
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-700 bg-white hover:bg-red-50 rounded-lg border border-red-300 transition-colors"
              >
                <FileDown size={12} /> PDF
              </button>
            </>
          )}
          <ChevronDown
            size={18}
            className={`text-red-800 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {expanded && (
        customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <CheckCircle size={32} className="mb-2 opacity-50" />
            <p className="text-sm">No missed renewals this month.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Customer</th>
                  <th className="px-4 py-3 text-left font-bold">Phone</th>
                  <th className="px-4 py-3 text-left font-bold hidden sm:table-cell">Package</th>
                  <th className="px-4 py-3 text-left font-bold hidden md:table-cell">Area</th>
                  <th className="px-4 py-3 text-left font-bold">Expiry</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {customers.map((c) => (
                  <tr key={c.id} className="hover:bg-red-50/40 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-bold text-gray-800">{c.username}</p>
                      <p className="text-xs text-gray-500">{c.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      {c.phone ? (
                        <a
                          href={`tel:${c.phone}`}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                        >
                          <Phone size={12} />
                          {c.phone}
                        </a>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{c.package_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{c.area_name || '—'}</td>
                    <td className="px-4 py-3 text-red-600 font-semibold">{c.expiry_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default ExpiredCustomersTable;
